import { ScorableQuestionGroupList } from "./Scorable";
import "./Assessment.less";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useEffect, useCallback, useState } from "react";
import windowEventService from "services/windowEventService";
import {
    selectHasAssessmentViewPermission,
    selectScorable,
    sendAssessmentAnswersData,
} from "./scorableSlice";
import { ScorableValidationContext } from "features/qualityForm/common/Contexts";
import FormType from "enums/FormType";
import { LearningAssessmentStatusEnum } from "@genesys/cloud-frontend/dist/mjs/models";
import {
    selectForm,
    selectFormType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { selectUser } from "features/auth/currentUserSlice";

export function Assessment() {
    const dispatch = useAppDispatch();
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [questionGroupToDisplay, setQuestionGroupToDisplay] = useState("");

    const onRequestFormAnswers = useCallback(
        (event: MessageEvent) => {
            const messageData = event.data;
            if (
                event.source === window.parent &&
                messageData.type == "request-form-answers"
            ) {
                setShowValidationErrors(
                    messageData.shouldValidateForSubmission
                );
                dispatch(sendAssessmentAnswersData());
            }
        },
        [dispatch]
    );

    const {
        status,
        formType,
        currentUser,
        reviewAssessmentResults,
        assignee,
        hasAssignmentViewPermission,
    } = useAppSelector((state) => {
        const status = selectScorable(state)?.status;
        const formType = selectFormType(state);
        const currentUser = selectUser(state);
        const { reviewAssessmentResults, assignee } =
            (selectForm(state) as any) ?? {};
        const hasAssignmentViewPermission =
            selectHasAssessmentViewPermission(state);

        return {
            status,
            formType,
            currentUser,
            reviewAssessmentResults,
            assignee,
            hasAssignmentViewPermission,
        };
    });

    const hasPermissionToReview =
        (reviewAssessmentResults?.byAssignees &&
            assignee === currentUser?.id) ||
        (reviewAssessmentResults?.byViewers && hasAssignmentViewPermission);

    const canReviewResults =
        formType === FormType.ASSESSMENT &&
        status === LearningAssessmentStatusEnum.Finished &&
        hasPermissionToReview;

    const onRequestToDisplayQuestionGroup = useCallback(
        (event: MessageEvent) => {
            const messageData = event.data;
            if (
                event.source === window.parent &&
                messageData.type == "set-question-group-to-display"
            ) {
                setQuestionGroupToDisplay(messageData.questionGroupToDisplay);
            }
        },
        []
    );

    useEffect(() => {
        windowEventService.registerEvent(
            "assessment-form-answers-event-listener",
            "message",
            onRequestFormAnswers
        );
        windowEventService.registerEvent(
            "assessment-form-change-question-group-event-listener",
            "message",
            onRequestToDisplayQuestionGroup
        );

        return () => {
            windowEventService.removeEvent(
                "assessment-form-answers-event-listener",
                "message"
            );
            windowEventService.removeEvent(
                "assessment-form-change-question-group-event-listener",
                "message"
            );
        };
    }, [onRequestFormAnswers, onRequestToDisplayQuestionGroup]);

    return (
        <div className="Assessment">
            <ScorableValidationContext.Provider
                value={{ showValidationErrors }}
            >
                <ScorableQuestionGroupList
                    questionGroupToDisplay={questionGroupToDisplay}
                    canReviewResults={canReviewResults}
                />
            </ScorableValidationContext.Provider>
        </div>
    );
}
